import React from 'react';
import type { BarberGrowthSnapshot } from '../../services/barberDataService';

/**
 * Piece 1 — Coaching nudges. Renders the barber's weakest Growth components as concrete plays
 * (with $/week for occupancy). Reads snapshot.nudges (computed at recompute). Null-safe: renders
 * nothing until a growth snapshot exists. Used on MyCoachingPage (barber) + CoachingDashboardPage (owner).
 * BEHAVIOR-PIN: SLIK-GROWTH-SYSTEM-BLUEPRINT.md · nudges from shared/growth.ts growthNudges().
 */
const TIER_COLOR: Record<string, string> = {
  gold: 'text-amber-600 dark:text-amber-400', silver: 'text-slate-500 dark:text-slate-400', bronze: 'text-orange-600 dark:text-orange-400', none: 'text-gray-500 dark:text-gray-400',
};

export const GrowthCoachingCard: React.FC<{ growth?: BarberGrowthSnapshot | null }> = ({ growth }) => {
  if (!growth) return null;
  const nudges = growth.nudges || [];
  return (
    <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900/20 p-4">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
        <h3 className="font-semibold text-gray-900 dark:text-white">📈 Growth focus</h3>
        <span className={`text-sm font-bold ${TIER_COLOR[growth.tier] || ''}`}>
          Score {growth.score} · Stage {growth.stage} {growth.stageName}
        </span>
      </div>
      {nudges.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">No growth focus yet — recompute to generate.</p>
      ) : (
        <ul className="space-y-2">
          {nudges.map((n, i) => (
            <li key={i} className="text-sm">
              <span className="font-medium text-gray-900 dark:text-white">{n.label}:</span>{' '}
              <span className="text-gray-700 dark:text-gray-300">{n.message}</span>
              {n.dollarPerWeek != null && n.dollarPerWeek > 0 && (
                <span className="ml-1 font-semibold text-green-700 dark:text-green-400">
                  ≈ ${n.dollarPerWeek.toLocaleString()}/wk
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GrowthCoachingCard;
